import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import CustomDropdown from "../resuableComponents/CustomDropdown";
import {
    FaPills,
    FaStethoscope,
    FaClipboardList,
    FaArrowRight,
    FaWallet,
    FaChartLine,
    FaUsers,
    FaUserPlus,
    FaFileInvoiceDollar,
    FaCheckCircle,
    FaExclamationCircle,
    FaMars,
    FaVenus,
    FaUsers as FaUsersIcon
} from "react-icons/fa";
import {
    getSalesAnalytics,
    getNewPatientRegistrations,
    getFinanceSummary,
    getPaymentModesBreakdown,
    getPendingAgingReport,
    getIpdSummary,
    getDashboardOverview,
    getRevenueAnalytics,
    getOpdCaseCount
} from "../../api/admin/dashboard";
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    AreaChart,
    Area,
    Legend,
    PieChart,
    Pie,
    Cell,
    BarChart,
    Bar,
    RadialBarChart,
    RadialBar,
    LabelList
} from "recharts";
import SkeletonLoader from "../resuableComponents/SkeletonLoader";
import "./AdminDashboard.css";

// Custom hook for intersection observer - Refined for better lazy loading
const useInView = (options = { threshold: 0.15, rootMargin: '0px 0px -50px 0px' }) => {
    const [isInView, setIsInView] = useState(false);
    const [hasBeenInView, setHasBeenInView] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) {
                setIsInView(true);
                setHasBeenInView(true);
            } else {
                setIsInView(false);
            }
        }, options);

        if (ref.current) {
            observer.observe(ref.current);
        }

        return () => {
            if (ref.current) observer.unobserve(ref.current);
        };
    }, [options]);

    return [ref, isInView, hasBeenInView];
};

const AdminDashboard = () => {
    const navigate = useNavigate();

    const [analytics, setAnalytics] = useState([]);
    const [finance, setFinance] = useState({ totalSales: 0, collected: 0, pending: 0, avgBillValue: 0 });
    const [patientFilter, setPatientFilter] = useState("monthly");
    const [patientRawData, setPatientRawData] = useState([]);
    const [patientsLoading, setPatientsLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('finance');
    const [financeView, setFinanceView] = useState('lifetime');
    const [selectedPeriod, setSelectedPeriod] = useState("");
    const [financeSummary, setFinanceSummary] = useState(null);
    const [paymentBreakdown, setPaymentBreakdown] = useState(null);
    const [pendingAging, setPendingAging] = useState(null);
    const [ipdStats, setIpdStats] = useState(null);
    const [overviewStats, setOverviewStats] = useState(null);
    const [revenueData, setRevenueData] = useState(null);
    const [revenueYear, setRevenueYear] = useState(new Date().getFullYear().toString());
    const [revenuePeriod, setRevenuePeriod] = useState((new Date().getMonth() + 1).toString());
    const [opdCaseData, setOpdCaseData] = useState([]);
    const [opdFilter, setOpdFilter] = useState("monthly");
    const [loading, setLoading] = useState(true);
    const [revenueLoading, setRevenueLoading] = useState(false);
    const [opdLoading, setOpdLoading] = useState(false);
    const [selectedGender, setSelectedGender] = useState("all");
    const [comparisonSales, setComparisonSales] = useState([]);
    const [comparisonPatients, setComparisonPatients] = useState([]);
    const [comparisonRevenue, setComparisonRevenue] = useState(null);
    const [comparisonOpd, setComparisonOpd] = useState([]);

    // Visibility Refs - Defining all necessary refs for scroll triggers
    const [headerRef, headerInView, headerVisible] = useInView({ threshold: 0.1 });
    const [statsRef, statsInView, statsVisible] = useInView();
    const [salesRef, salesInView, salesVisible] = useInView();
    const [opdRef, opdInView, opdVisible] = useInView();
    const [revenueRef, revenueInView, revenueVisible] = useInView();
    const [patientsRef, patientsInView, patientsVisible] = useInView();
    const [pendingAgingRef, pendingAgingInView, pendingAgingVisible] = useInView();
    const [collectionRef, collectionInView, collectionVisible] = useInView();
    const [summaryRef, summaryInView, summaryVisible] = useInView();
    const [financeGridRef, financeGridInView, financeGridVisible] = useInView();
    const [invoiceRef, invoiceInView, invoiceVisible] = useInView();
    const [revenueGridRef, revenueGridInView, revenueGridVisible] = useInView();

    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

    useEffect(() => {
        const fetchDashboardData = async () => {
            setLoading(true);
            try {
                const [
                    salesRes,
                    summaryRes,
                    paymentRes,
                    pendingAgingRes,
                    ipdSummaryRes,
                    dashboardOverviewRes,
                    maleSalesRes,
                    femaleSalesRes
                ] = await Promise.all([
                    getSalesAnalytics(selectedGender),
                    getFinanceSummary(selectedGender),
                    getPaymentModesBreakdown(selectedGender),
                    getPendingAgingReport(selectedGender),
                    getIpdSummary(selectedGender),
                    getDashboardOverview(selectedGender),
                    selectedGender === 'all' ? getSalesAnalytics('male') : null,
                    selectedGender === 'all' ? getSalesAnalytics('female') : null
                ]);

                // 1. Process Sales Analytics
                const salesData = salesRes?.data?.data?.sales || salesRes?.data?.sales;
                if (salesData && Array.isArray(salesData)) {
                    let totalSales = 0;
                    let totalCollected = 0;
                    let totalPending = 0;
                    let count = 0;

                    const formattedAnalytics = salesData.map(item => {
                        totalSales += Number(item.totalSales) || 0;
                        totalCollected += Number(item.collected) || 0;
                        totalPending += Number(item.pending) || 0;
                        count++;

                        const [year, month] = item.period.split("-");
                        return {
                            ...item,
                            name: `${monthNames[parseInt(month) - 1]} ${year.slice(2)}`,
                        };
                    });

                    setAnalytics(formattedAnalytics);
                    setFinance({
                        totalSales,
                        collected: totalCollected,
                        pending: totalPending,
                        avgBillValue: count > 0 ? totalSales / count : 0
                    });

                    if (formattedAnalytics.length > 0 && !selectedPeriod) {
                        setSelectedPeriod(formattedAnalytics[formattedAnalytics.length - 1].period);
                    }
                }

                // 1.1 Process Comparison Sales
                if (selectedGender === 'all' && maleSalesRes?.data?.data?.sales && femaleSalesRes?.data?.data?.sales) {
                    const mSales = maleSalesRes.data.data.sales;
                    const fSales = femaleSalesRes.data.data.sales;

                    const allPeriods = new Map();
                    mSales.forEach(m => allPeriods.set(m.period, { period: m.period, male: Number(m.totalSales) || 0, female: 0 }));
                    fSales.forEach(f => {
                        if (allPeriods.has(f.period)) {
                            allPeriods.get(f.period).female = Number(f.totalSales) || 0;
                        } else {
                            allPeriods.set(f.period, { period: f.period, male: 0, female: Number(f.totalSales) || 0 });
                        }
                    });

                    const merged = Array.from(allPeriods.values())
                        .sort((a, b) => a.period.localeCompare(b.period))
                        .map(d => {
                            const [year, month] = d.period.split("-");
                            return {
                                ...d,
                                name: `${monthNames[parseInt(month) - 1]} ${year.slice(2)}`,
                            };
                        });
                    setComparisonSales(merged);
                } else {
                    setComparisonSales([]);
                }

                // 2. Removed initial patient registration processing (moved to separate effect)

                // 3. Set other stats
                setFinanceSummary(summaryRes?.data?.data || summaryRes?.data);
                setPaymentBreakdown(paymentRes?.data?.data || paymentRes?.data);
                setPendingAging(pendingAgingRes?.data?.data);
                setIpdStats(ipdSummaryRes?.data?.data);
                setOverviewStats(dashboardOverviewRes?.data?.data);
            } catch (error) {
                console.error("Failed to fetch dashboard data:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchDashboardData();
    }, [selectedGender]);

    // Lazy load effects - triggered by visibility
    useEffect(() => {
        const fetchRevenueData = async () => {
            setRevenueLoading(true);
            try {
                const [allRes, maleRes, femaleRes] = await Promise.all([
                    getRevenueAnalytics("month", revenueYear, revenuePeriod, selectedGender),
                    selectedGender === 'all' ? getRevenueAnalytics("month", revenueYear, revenuePeriod, 'male') : null,
                    selectedGender === 'all' ? getRevenueAnalytics("month", revenueYear, revenuePeriod, 'female') : null
                ]);

                if (allRes.data?.success) setRevenueData(allRes.data.data);
                if (selectedGender === 'all' && maleRes?.data?.success && femaleRes?.data?.success) {
                    setComparisonRevenue({ male: maleRes.data.data, female: femaleRes.data.data });
                } else {
                    setComparisonRevenue(null);
                }
            } catch (err) { console.error(err); } finally { setRevenueLoading(false); }
        };
        if (revenueVisible) fetchRevenueData();
    }, [revenueYear, revenuePeriod, revenueVisible, selectedGender]);

    useEffect(() => {
        const fetchOpdCases = async () => {
            setOpdLoading(true);
            try {
                const [allRes, maleRes, femaleRes] = await Promise.all([
                    getOpdCaseCount(opdFilter, selectedGender),
                    selectedGender === 'all' ? getOpdCaseCount(opdFilter, 'male') : null,
                    selectedGender === 'all' ? getOpdCaseCount(opdFilter, 'female') : null
                ]);

                if (allRes.data?.success) setOpdCaseData(allRes.data.data);

                if (selectedGender === 'all' && maleRes?.data?.success && femaleRes?.data?.success) {
                    const mData = maleRes.data.data;
                    const fData = femaleRes.data.data;

                    // Create a unique key for each period based on filter type
                    const getPeriodKey = (p) => {
                        if (opdFilter === 'monthly') return `${p.year}-${p.month}`;
                        if (opdFilter === 'yearly') return `${p.year}`;
                        return `${p.year}-${p.month}-${p.day}`;
                    };

                    const allPeriods = new Map();
                    mData.forEach(m => allPeriods.set(getPeriodKey(m.period), { period: m.period, male: m.count, female: 0 }));
                    fData.forEach(f => {
                        const key = getPeriodKey(f.period);
                        if (allPeriods.has(key)) {
                            allPeriods.get(key).female = f.count;
                        } else {
                            allPeriods.set(key, { period: f.period, male: 0, female: f.count });
                        }
                    });

                    // Sort periods chronologically
                    const sortedData = Array.from(allPeriods.values()).sort((a, b) => {
                        if (a.period.year !== b.period.year) return a.period.year - b.period.year;
                        if (a.period.month !== b.period.month) return a.period.month - b.period.month;
                        return (a.period.day || 0) - (b.period.day || 0);
                    });

                    const merged = sortedData.map(d => ({
                        name: opdFilter === 'monthly' ? `${monthNames[d.period.month - 1]} ${d.period.year}` : opdFilter === 'yearly' ? `${d.period.year}` : `${d.period.day} ${monthNames[d.period.month - 1]}`,
                        male: d.male,
                        female: d.female
                    }));
                    setComparisonOpd(merged);
                } else {
                    setComparisonOpd([]);
                }
            } catch (err) { console.error(err); } finally { setOpdLoading(false); }
        };
        if (opdVisible) fetchOpdCases();
    }, [opdFilter, opdVisible, selectedGender]);

    useEffect(() => {
        const fetchPatientRegistrations = async () => {
            setPatientsLoading(true);
            try {
                const [allRes, maleRes, femaleRes] = await Promise.all([
                    getNewPatientRegistrations(patientFilter, selectedGender),
                    selectedGender === 'all' ? getNewPatientRegistrations(patientFilter, 'male') : null,
                    selectedGender === 'all' ? getNewPatientRegistrations(patientFilter, 'female') : null
                ]);

                const data = allRes?.data?.data || allRes?.data;
                if (Array.isArray(data)) setPatientRawData(data);

                if (selectedGender === 'all' && (maleRes?.data?.data || maleRes?.data) && (femaleRes?.data?.data || femaleRes?.data)) {
                    const mData = maleRes?.data?.data || maleRes?.data;
                    const fData = femaleRes?.data?.data || femaleRes?.data;

                    const getPeriodKey = (p) => {
                        if (patientFilter === 'monthly') return `${p.year}-${p.month}`;
                        if (patientFilter === 'yearly') return `${p.year}`;
                        return `${p.year}-${p.month}-${p.day}`;
                    };

                    const allPeriods = new Map();
                    mData.forEach(m => allPeriods.set(getPeriodKey(m.period), { period: m.period, male: m.count, female: 0 }));
                    fData.forEach(f => {
                        const key = getPeriodKey(f.period);
                        if (allPeriods.has(key)) {
                            allPeriods.get(key).female = f.count;
                        } else {
                            allPeriods.set(key, { period: f.period, male: 0, female: f.count });
                        }
                    });

                    // Sort periods chronologically
                    const sortedData = Array.from(allPeriods.values()).sort((a, b) => {
                        if (a.period.year !== b.period.year) return a.period.year - b.period.year;
                        if (a.period.month !== b.period.month) return a.period.month - b.period.month;
                        return (a.period.day || 0) - (b.period.day || 0);
                    });

                    const merged = sortedData.map(d => ({
                        name: patientFilter === 'monthly' ? `${monthNames[d.period.month - 1]} ${d.period.year.toString().slice(2)}` : patientFilter === 'yearly' ? `${d.period.year}` : `${d.period.day} ${monthNames[d.period.month - 1]}`,
                        male: d.male,
                        female: d.female
                    }));
                    setComparisonPatients(merged);
                } else {
                    setComparisonPatients([]);
                }
            } catch (err) { console.error(err); } finally { setPatientsLoading(false); }
        };
        if (patientsVisible) fetchPatientRegistrations();
    }, [patientFilter, patientsVisible, selectedGender]);

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            maximumFractionDigits: 0
        }).format(amount);
    };

    const selectedMonthData = analytics.find(item => item.period === selectedPeriod);

    const financeCards = [
        { label: "Total Sales", value: selectedMonthData?.totalSales || 0, icon: <FaWallet />, color: "#4f46e5" },
        { label: "OPD Sales", value: selectedMonthData?.opdSales || 0, icon: <FaChartLine />, color: "#3b82f6" },
        { label: "IPD Sales", value: selectedMonthData?.ipdSales || 0, icon: <FaChartLine />, color: "#8b5cf6" },
        { label: "Collected", value: selectedMonthData?.collected || 0, icon: <FaChartLine />, color: "#10b981" },
        { label: "Pending", value: selectedMonthData?.pending || 0, icon: <FaWallet />, color: "#f59e0b" },
        { label: "Avg Bill", value: selectedMonthData?.avgBillValue || 0, icon: <FaChartLine />, color: "#ef4444" },
    ];

    const summaryCards = [
        { label: "Revenue", value: finance.totalSales || financeSummary?.revenue?.total || 0, subLabel: "Overall sales generated", icon: <FaChartLine />, color: "#4f46e5" },
        { label: "Collected", value: finance.collected || financeSummary?.collection?.totalCollected || 0, subLabel: "Actual payments received", icon: <FaCheckCircle />, color: "#10b981" },
        { label: "Pending", value: finance.pending || financeSummary?.pending?.totalPending || 0, subLabel: "Outstanding receivables", icon: <FaWallet />, color: "#f59e0b" },
    ];

    const totalInvoices = financeSummary?.invoiceStats?.totalInvoices || 0;
    const paidInvoices = financeSummary?.invoiceStats?.paidInvoices || 0;
    const unpaidInvoices = financeSummary?.invoiceStats?.unpaidInvoices || 0;

    const invoiceStats = [
        { label: "Total Invoices", count: totalInvoices, icon: <FaFileInvoiceDollar />, color: "#6366f1", bg: "#e0e7ff", desc: "All time records" },
        { label: "Paid Invoices", count: paidInvoices, icon: <FaCheckCircle />, color: "#10b981", bg: "#dcfce7", percent: totalInvoices > 0 ? Math.round((paidInvoices / totalInvoices) * 100) : 0, desc: "Successfully cleared" },
        { label: "Pending Invoices", count: unpaidInvoices, icon: <FaExclamationCircle />, color: "#ef4444", bg: "#fee2e2", percent: totalInvoices > 0 ? Math.round((unpaidInvoices / totalInvoices) * 100) : 0, desc: "Action required" },
    ];

    const lifetimeRadialData = [
        { name: 'Pending', value: financeSummary?.pending?.totalPending || 0, fill: '#f59e0b' },
        { name: 'Collected', value: financeSummary?.collection?.totalCollected || 0, fill: '#10b981' },
        { name: 'Revenue', value: financeSummary?.revenue?.total || 0, fill: '#4f46e5' }
    ];

    const paymentCompositionData = [
        { name: 'Cash', value: paymentBreakdown?.cash || 0, color: '#22c55e' },
        { name: 'Card', value: paymentBreakdown?.card || 0, color: '#3b82f6' },
        { name: 'UPI', value: paymentBreakdown?.upi || 0, color: '#8b5cf6' },
        { name: 'Insurance', value: paymentBreakdown?.insurance || 0, color: '#f59e0b' }
    ];

    const monthlyPieData = [
        { name: 'OPD Sales', value: selectedMonthData?.opdSales || 0, color: '#3b82f6' },
        { name: 'IPD Sales', value: selectedMonthData?.ipdSales || 0, color: '#8b5cf6' }
    ];

    const revenueChartData = [];
    if (revenueData) {
        monthNames.forEach((monthName, index) => {
            if (selectedGender === 'all' && comparisonRevenue) {
                const mOpd = comparisonRevenue.male.opd?.find(i => i._id.month === index + 1)?.amount || 0;
                const mIpd = comparisonRevenue.male.ipd?.find(i => i._id.month === index + 1)?.amount || 0;
                const fOpd = comparisonRevenue.female.opd?.find(i => i._id.month === index + 1)?.amount || 0;
                const fIpd = comparisonRevenue.female.ipd?.find(i => i._id.month === index + 1)?.amount || 0;
                revenueChartData.push({
                    name: monthName,
                    Male: mOpd + mIpd,
                    Female: fOpd + fIpd,
                    OPD: mOpd + fOpd,
                    IPD: mIpd + fIpd,
                    Total: mOpd + mIpd + fOpd + fIpd
                });
            } else {
                const opdItem = revenueData.opd?.find(i => i._id.month === index + 1);
                const ipdItem = revenueData.ipd?.find(i => i._id.month === index + 1);
                const opdAmount = opdItem?.amount || 0;
                const ipdAmount = ipdItem?.amount || 0;
                revenueChartData.push({ name: monthName, OPD: opdAmount, IPD: ipdAmount, Total: opdAmount + ipdAmount });
            }
        });
    }

    const processedOpdData = (opdCaseData || []).map(item => ({
        name: opdFilter === 'monthly' ? `${monthNames[item.period.month - 1]} ${item.period.year}` : opdFilter === 'yearly' ? `${item.period.year}` : `${item.period.day} ${monthNames[item.period.month - 1]}`,
        count: item.count
    }));

    const processedPatientData = React.useMemo(() => {
        if (!Array.isArray(patientRawData)) return [];
        // Optional: Sort if needed. Assuming API returns mostly sorted, but playing safe.
        const sorted = [...patientRawData].sort((a, b) => {
            if (patientFilter === 'monthly') {
                if (a.period.year !== b.period.year) return a.period.year - b.period.year;
                return a.period.month - b.period.month;
            } else if (patientFilter === 'yearly') {
                return a.period.year - b.period.year;
            } else { // daily
                const dateA = new Date(a.period.year, a.period.month - 1, a.period.day);
                const dateB = new Date(b.period.year, b.period.month - 1, b.period.day);
                return dateA - dateB;
            }
        });

        return sorted.map(item => ({
            name: patientFilter === 'monthly'
                ? `${monthNames[item.period.month - 1]} ${item.period.year.toString().slice(2)}`
                : patientFilter === 'yearly'
                    ? `${item.period.year}`
                    : `${item.period.day} ${monthNames[item.period.month - 1]}`,
            count: item.count
        }));
    }, [patientRawData, patientFilter]);

    return (
        <div className="admin-dashboard">
            <div className="admin-dashboard-frame">
                <section className="admin-dashboard-hero">
                    <div className="hero-content">
                        <h1>Admin Command Center</h1>
                        <p>
                            Monitor your hospital's performance through real-time financial analytics
                            and patient registration trends.
                        </p>
                    </div>

                    <div className="hero-actions" style={{ marginTop: '1rem' }}>
                        <div className="finance-sub-navigation" style={{ margin: 0, padding: '0.25rem' }}>
                            <button
                                className={`sub-nav-btn ${selectedGender === 'all' ? 'active' : ''}`}
                                onClick={() => setSelectedGender('all')}
                                style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                            >
                                <FaUsersIcon /> All Sections
                            </button>
                            <button
                                className={`sub-nav-btn ${selectedGender === 'male' ? 'active' : ''}`}
                                onClick={() => setSelectedGender('male')}
                                style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                            >
                                <FaMars style={{ color: selectedGender === 'male' ? '#3b82f6' : 'inherit' }} /> Male Section
                            </button>
                            <button
                                className={`sub-nav-btn ${selectedGender === 'female' ? 'active' : ''}`}
                                onClick={() => setSelectedGender('female')}
                                style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                            >
                                <FaVenus style={{ color: selectedGender === 'female' ? '#ec4899' : 'inherit' }} /> Female Section
                            </button>
                        </div>
                    </div>
                </section>

                {/* Quick Stats Row */}
                <div className="finance-grid" style={{ marginBottom: '2rem', marginTop: '1.5rem' }} ref={statsRef}>
                    {loading ? Array.from({ length: 4 }).map((_, i) => <SkeletonLoader key={i} variant="dashboard-summary-card" />) : (
                        overviewStats && (
                            <>
                                <div className={`finance-card animate-slide-up stagger-1 ${statsVisible ? 'visible' : ''}`}>
                                    <div className="finance-card-icon" style={{ backgroundColor: '#e0e7ff', color: '#4f46e5' }}><FaWallet /></div>
                                    <div className="finance-card-info">
                                        <p>Revenue (Current)</p>
                                        <h3>{formatCurrency(overviewStats.finance?.totalRevenue || 0)}</h3>
                                        <span className="card-sub-info">OPD: {formatCurrency(overviewStats.finance?.opdRevenue || 0)}</span>
                                    </div>
                                </div>
                                <div className={`finance-card animate-slide-up stagger-2 ${statsVisible ? 'visible' : ''}`}>
                                    <div className="finance-card-icon" style={{ backgroundColor: '#dcfce7', color: '#10b981' }}><FaUsers /></div>
                                    <div className="finance-card-info">
                                        <p>New Patients</p>
                                        <h3>{overviewStats.patients?.newThisMonth || 0}</h3>
                                        <span className="card-sub-info">This Month</span>
                                    </div>
                                </div>
                                <div className={`finance-card animate-slide-up stagger-3 ${statsVisible ? 'visible' : ''}`}>
                                    <div className="finance-card-icon" style={{ backgroundColor: '#f3e8ff', color: '#8b5cf6' }}><FaStethoscope /></div>
                                    <div className="finance-card-info">
                                        <p>Active IPD</p>
                                        <h3>{overviewStats.cases?.ipdActive || 0}</h3>
                                        <span className="card-sub-info">Current In-Patients</span>
                                    </div>
                                </div>
                                <div className={`finance-card animate-slide-up stagger-4 ${statsVisible ? 'visible' : ''}`}>
                                    <div className="finance-card-icon" style={{ backgroundColor: '#fee2e2', color: '#ef4444' }}><FaExclamationCircle /></div>
                                    <div className="finance-card-info">
                                        <p>Total Pending</p>
                                        <h3>{formatCurrency(overviewStats.finance?.pendingAmount || 0)}</h3>
                                        <span className="card-sub-info">Attention Required</span>
                                    </div>
                                </div>
                            </>
                        )
                    )}
                </div>

                <div className="dashboard-tabs">
                    <button className={`tab-button ${activeTab === 'finance' ? 'active' : ''}`} onClick={() => setActiveTab('finance')}><FaWallet /> Financial Analytics</button>
                    <button className={`tab-button ${activeTab === 'patients' ? 'active' : ''}`} onClick={() => setActiveTab('patients')}><FaUsers /> Patient Growth</button>
                    <button className={`tab-button ${activeTab === 'revenue' ? 'active' : ''}`} onClick={() => setActiveTab('revenue')}><FaChartLine /> Rate Analytics</button>
                </div>

                {activeTab === 'finance' && (
                    <div className="tab-content-fade-in">
                        <div className="finance-sub-navigation">
                            <button className={`sub-nav-btn ${financeView === 'lifetime' ? 'active' : ''}`} onClick={() => setFinanceView('lifetime')}>Overview</button>
                            <button className={`sub-nav-btn ${financeView === 'monthly' ? 'active' : ''}`} onClick={() => setFinanceView('monthly')}>Monthly Breakdown</button>
                        </div>

                        {financeView === 'lifetime' ? (
                            <section className="finance-overview">
                                <header className="section-header">
                                    <h2>Financial Overview</h2>
                                    <p>Overall hospital revenue and collection status</p>
                                </header>
                                <div className="finance-grid" ref={summaryRef}>
                                    {loading ? Array.from({ length: 3 }).map((_, i) => <SkeletonLoader key={i} variant="dashboard-summary-card" />) :
                                        summaryCards.map((card, i) => (
                                            <div key={i} className={`finance-card animate-slide-up stagger-${i + 1} ${summaryVisible ? 'visible' : ''}`} style={{ "--card-color": card.color }}>
                                                <div className="finance-card-icon">{card.icon}</div>
                                                <div className="finance-card-info">
                                                    <p>{card.label}</p>
                                                    <h3>{formatCurrency(card.value)}</h3>
                                                    <span className="card-sub-info">{card.subLabel}</span>
                                                </div>
                                            </div>
                                        ))
                                    }
                                </div>

                                {/* Sales Trend Chart */}
                                <div className={`lifetime-chart-box full-width-chart animate-slide-up ${salesVisible ? 'visible' : ''}`} ref={salesRef}>
                                    {loading ? <SkeletonLoader variant="chart-placeholder" /> : (
                                        <>
                                            <header className="content-header-mini">
                                                <h3>Sales Trend</h3>
                                                <p>Revenue over time</p>
                                            </header>
                                            <div className="chart-container" style={{ height: 300 }}>
                                                {salesVisible && (
                                                    <ResponsiveContainer width="100%" height="100%">
                                                        <AreaChart data={selectedGender === 'all' ? comparisonSales : analytics}>
                                                            <defs>
                                                                <linearGradient id="colorSales" x1="0" y1="0" x2="0" y2="1">
                                                                    <stop offset="5%" stopColor="#4f46e5" stopOpacity={0.1} />
                                                                    <stop offset="95%" stopColor="#4f46e5" stopOpacity={0} />
                                                                </linearGradient>
                                                                <linearGradient id="colorMale" x1="0" y1="0" x2="0" y2="1">
                                                                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.1} />
                                                                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                                                                </linearGradient>
                                                                <linearGradient id="colorFemale" x1="0" y1="0" x2="0" y2="1">
                                                                    <stop offset="5%" stopColor="#ec4899" stopOpacity={0.1} />
                                                                    <stop offset="95%" stopColor="#ec4899" stopOpacity={0} />
                                                                </linearGradient>
                                                            </defs>
                                                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                                            <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#64748b', fontSize: 12 }} dy={10} />
                                                            <YAxis axisLine={false} tickLine={false} tick={{ fill: '#64748b', fontSize: 12 }} tickFormatter={(val) => `₹${val / 1000}k`} />
                                                            <Tooltip contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)' }} formatter={(val) => formatCurrency(val)} />
                                                            <Legend verticalAlign="top" height={36} align="right" />
                                                            {selectedGender === 'all' ? (
                                                                <>
                                                                    <Area type="monotone" dataKey="male" stroke="#3b82f6" strokeWidth={3} fillOpacity={1} fill="url(#colorMale)" name="Male Sales" animationDuration={1500} isAnimationActive={salesVisible} />
                                                                    <Area type="monotone" dataKey="female" stroke="#ec4899" strokeWidth={3} fillOpacity={1} fill="url(#colorFemale)" name="Female Sales" animationDuration={1500} isAnimationActive={salesVisible} />
                                                                </>
                                                            ) : (
                                                                <Area type="monotone" dataKey="totalSales" stroke="#4f46e5" strokeWidth={3} fillOpacity={1} fill="url(#colorSales)" name="Total Sales" animationDuration={1500} isAnimationActive={salesVisible} />
                                                            )}
                                                        </AreaChart>
                                                    </ResponsiveContainer>
                                                )}
                                            </div>
                                        </>
                                    )}
                                </div>

                                <div className="lifetime-charts-row" style={{ marginTop: '2rem', marginBottom: '2rem' }}>
                                    {/* Pending Aging Chart */}
                                    <div className={`lifetime-chart-box animate-slide-up ${pendingAgingVisible ? 'visible' : ''}`} ref={pendingAgingRef}>
                                        <header className="content-header-mini">
                                            <h3>Pending Payment Aging</h3>
                                            <p>Receivables breakdown by duration</p>
                                        </header>
                                        <div className="bar-chart-wrapper">
                                            {pendingAgingVisible && (
                                                <ResponsiveContainer width="100%" height={260}>
                                                    <BarChart
                                                        data={[{ name: '0-7 Days', value: pendingAging?.['0_7_days'] || 0, fill: '#10b981' }, { name: '8-30 Days', value: pendingAging?.['8_30_days'] || 0, fill: '#f59e0b' }, { name: '30+ Days', value: pendingAging?.['30_plus_days'] || 0, fill: '#ef4444' }]}
                                                        layout="vertical"
                                                        margin={{ left: 10, right: 70, top: 10, bottom: 0 }}
                                                    >
                                                        <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f1f5f9" />
                                                        <XAxis type="number" hide />
                                                        <YAxis dataKey="name" type="category" axisLine={false} tickLine={false} tick={{ fill: '#64748b', fontSize: 12, fontWeight: 600 }} width={80} />
                                                        <Tooltip cursor={{ fill: 'rgba(0, 0, 0, 0.02)' }} contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)' }} formatter={(val) => formatCurrency(val)} />
                                                        <Bar dataKey="value" radius={[0, 6, 6, 0]} barSize={24} animationDuration={1500} animationEasing="ease-in-out" isAnimationActive={pendingAgingVisible}>
                                                            <LabelList dataKey="value" position="right" formatter={(val) => formatCurrency(val)} style={{ fontSize: '12px', fontWeight: 600, fill: '#64748b' }} />
                                                            <Cell fill="#10b981" /><Cell fill="#f59e0b" /><Cell fill="#ef4444" />
                                                        </Bar>
                                                    </BarChart>
                                                </ResponsiveContainer>
                                            )}
                                        </div>
                                    </div>

                                    {/* IPD Operational Stats */}
                                    <div className={`lifetime-chart-box animate-slide-up ${summaryVisible ? 'visible' : ''}`}>
                                        <header className="content-header-mini">
                                            <h3>IPD Flow Summary</h3>
                                            <p>Patient admissions and discharges</p>
                                        </header>
                                        <div className="metrics-detailed-grid" style={{ marginTop: '1rem', gridTemplateColumns: '1fr' }}>
                                            {ipdStats && [
                                                { label: "Total Admissions", value: ipdStats.totalAdmissions, color: "#4f46e5", icon: <FaUserPlus /> },
                                                { label: "Total Discharges", value: ipdStats.totalDischarges, color: "#10b981", icon: <FaCheckCircle /> },
                                                { label: "Active Patients", value: ipdStats.activePatients, color: "#8b5cf6", icon: <FaStethoscope /> },
                                            ].map((stat, idx) => (
                                                <div key={idx} className={`animate-slide-up stagger-${idx + 1} ${summaryVisible ? 'visible' : ''}`} style={{ display: 'flex', alignItems: 'center', padding: '1rem', borderRadius: '12px', background: '#f8fafc', border: '1px solid #f1f5f9', gap: '1rem' }}>
                                                    <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: 'white', color: stat.color, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>{stat.icon}</div>
                                                    <div><span style={{ fontSize: '0.85rem', color: '#64748b', fontWeight: 600, display: 'block' }}>{stat.label}</span><span style={{ fontSize: '1.25rem', color: '#1e293b', fontWeight: 700 }}>{stat.value}</span></div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Collection Distribution */}
                                    <div className={`lifetime-chart-box animate-slide-up ${collectionVisible ? 'visible' : ''}`} ref={collectionRef}>
                                        <header className="content-header-mini">
                                            <h3>Collection Distribution</h3>
                                            <p>Total Revenue vs Collected vs Pending</p>
                                        </header>
                                        <div className="bar-chart-wrapper">
                                            {collectionVisible && (
                                                <ResponsiveContainer width="100%" height={260}>
                                                    <BarChart layout="vertical" data={lifetimeRadialData} margin={{ left: 20, right: 80, top: 10 }}>
                                                        <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f1f5f9" />
                                                        <XAxis type="number" hide />
                                                        <YAxis dataKey="name" type="category" axisLine={false} tickLine={false} tick={{ fill: '#64748b', fontSize: 12, fontWeight: 700 }} width={100} />
                                                        <Tooltip cursor={{ fill: 'rgba(0, 0, 0, 0.02)' }} contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)' }} formatter={(val) => formatCurrency(val)} />
                                                        <Bar dataKey="value" radius={[0, 12, 12, 0]} barSize={36} animationDuration={1500} animationEasing="ease-in-out" isAnimationActive={collectionInView}>
                                                            <LabelList dataKey="value" position="right" formatter={(val) => formatCurrency(val)} style={{ fontSize: '12px', fontWeight: 600, fill: '#64748b' }} />
                                                            {lifetimeRadialData.map((entry, index) => <Cell key={index} fill={entry.fill} />)}
                                                        </Bar>
                                                    </BarChart>
                                                </ResponsiveContainer>
                                            )}
                                        </div>
                                    </div>

                                    {/* Payment Composition */}
                                    <div className={`lifetime-chart-box animate-slide-up ${collectionVisible ? 'visible' : ''}`}>
                                        <header className="content-header-mini">
                                            <h3>Payment Composition</h3>
                                            <p>Revenue split by payment mode</p>
                                        </header>
                                        <div className="pie-chart-wrapper">
                                            {collectionVisible && (
                                                <ResponsiveContainer width="100%" height={260}>
                                                    <PieChart>
                                                        <Pie data={paymentCompositionData} innerRadius={70} outerRadius={95} paddingAngle={1} dataKey="value" animationDuration={1500} animationEasing="ease-in-out" isAnimationActive={collectionInView}>
                                                            {paymentCompositionData.map((entry, index) => <Cell key={index} fill={entry.color} />)}
                                                        </Pie>
                                                        <Tooltip contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)' }} formatter={(val) => formatCurrency(val)} />
                                                        <Legend verticalAlign="bottom" height={40} iconType="circle" />
                                                    </PieChart>
                                                </ResponsiveContainer>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <section className="invoice-status-section" ref={invoiceRef}>
                                    <header className="content-header-mini">
                                        <h3>Invoice Records</h3>
                                        <p>Comprehensive tracking of all payment vouchers</p>
                                    </header>
                                    <div className="invoice-stats-grid-free">
                                        {loading ? Array.from({ length: 3 }).map((_, i) => <SkeletonLoader key={i} variant="invoice-stat-card" />) :
                                            invoiceStats.map((stat, idx) => (
                                                <div key={idx} className={`invoice-stat-card-free animate-slide-up stagger-${idx + 1} ${invoiceVisible ? 'visible' : ''}`} style={{ borderColor: stat.color }}>
                                                    <div className="stat-icon-box-free" style={{ background: stat.bg, color: stat.color }}>{stat.icon}</div>
                                                    <div className="stat-info-free" style={{ width: '100%' }}>
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                            <span className="stat-label-free">{stat.label}</span>
                                                            {stat.percent !== undefined && <span style={{ fontSize: '0.8rem', fontWeight: 600, color: stat.color, background: stat.bg, padding: '2px 8px', borderRadius: '12px' }}>{stat.percent}%</span>}
                                                        </div>
                                                        <h3 className="stat-value-free">{stat.count}</h3>
                                                        <p style={{ margin: '4px 0 0 0', fontSize: '0.8rem', color: '#94a3b8' }}>{stat.desc}</p>
                                                        {stat.percent !== undefined && (
                                                            <div style={{ width: '100%', height: '6px', background: '#f1f5f9', borderRadius: '3px', marginTop: '12px', overflow: 'hidden' }}>
                                                                <div style={{ width: `${stat.percent}%`, height: '100%', background: stat.color, borderRadius: '3px', transition: 'width 1s ease' }} />
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            ))
                                        }
                                    </div>
                                </section>
                            </section>
                        ) : (
                            <section className="dashboard-section monthly-section">
                                <header className="analysis-header">
                                    <div className="analysis-title">
                                        <div className="title-icon"><FaChartLine /></div>
                                        <div><h2>Monthly Analytics</h2><p>Performance breakdown for selected month</p></div>
                                    </div>
                                    <div className="analysis-filter">
                                        <CustomDropdown label="Analysis Period" value={selectedPeriod} onChange={(val) => setSelectedPeriod(typeof val === 'object' ? val.target.value : val)} options={analytics.map(item => ({ value: item.period, label: item.name }))} style={{ minWidth: '200px', backgroundColor: 'white' }} />
                                    </div>
                                </header>

                                <div className="monthly-analysis-content" ref={financeGridRef}>
                                    <div className="metrics-detailed-grid">
                                        {loading ? Array.from({ length: 6 }).map((_, i) => <SkeletonLoader key={i} variant="dashboard-summary-card" />) :
                                            financeCards.map((card, idx) => (
                                                <div key={idx} className={`metric-spec-card animate-slide-up stagger-${idx + 1} ${financeGridVisible ? 'visible' : ''}`} style={{ "--metric-color": card.color }}>
                                                    <div className="metric-header"><div className="metric-icon-box">{card.icon}</div><span className="metric-label">{card.label}</span></div>
                                                    <div className="metric-value-container">
                                                        <h3 className="metric-value">{formatCurrency(card.value)}</h3>
                                                        <div className="metric-indicator-bar" style={{ width: '100%', height: '4px', background: `${card.color}15`, borderRadius: '2px', marginTop: '1rem', overflow: 'hidden' }}>
                                                            <div className="indicator-progress" style={{ width: '60%', height: '100%', background: card.color, borderRadius: '2px' }} />
                                                        </div>
                                                    </div>
                                                </div>
                                            ))
                                        }
                                    </div>
                                    <div className={`monthly-chart-aside animate-slide-up ${financeGridVisible ? 'visible' : ''}`}>
                                        <header className="content-header-mini"><h3>Sales Composition</h3><p>OPD vs IPD Distribution</p></header>
                                        <div className="pie-chart-wrapper">
                                            {financeGridVisible && (
                                                <ResponsiveContainer width="100%" height={240}>
                                                    <PieChart>
                                                        <Pie data={monthlyPieData} innerRadius={60} outerRadius={80} paddingAngle={1} dataKey="value" animationDuration={1500} animationEasing="ease-in-out" isAnimationActive={financeGridVisible}>
                                                            {monthlyPieData.map((entry, index) => <Cell key={index} fill={entry.color} />)}
                                                        </Pie>
                                                        <Tooltip formatter={(val) => formatCurrency(val)} />
                                                        <Legend verticalAlign="bottom" height={36} />
                                                    </PieChart>
                                                </ResponsiveContainer>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </section>
                        )}
                    </div>
                )}

                {activeTab === 'patients' && (
                    <div className="tab-content-fade-in">
                        <section>
                            <div className={`lifetime-chart-box full-width-chart animate-slide-up ${patientsVisible ? 'visible' : ''}`} style={{ marginBottom: '2rem' }} ref={patientsRef}>
                                <header className="content-header-mini" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <div><h3>Patient Growth</h3><p>Registration trend across all clinics</p></div>
                                    <div className="finance-sub-navigation" style={{ margin: 0 }}>
                                        {['daily', 'monthly', 'yearly'].map(filter => <button key={filter} className={`sub-nav-btn ${patientFilter === filter ? 'active' : ''}`} onClick={() => setPatientFilter(filter)} style={{ padding: '0.4rem 0.8rem', fontSize: '0.85rem' }}>{filter.charAt(0).toUpperCase() + filter.slice(1)}</button>)}
                                    </div>
                                </header>
                                <div className="chart-container">
                                    {patientsLoading ? <SkeletonLoader variant="chart-placeholder" /> : (patientsVisible && (selectedGender === 'all' ? comparisonPatients : processedPatientData).length > 0) ? (
                                        <ResponsiveContainer width="100%" height={350}>
                                            <LineChart data={selectedGender === 'all' ? comparisonPatients : processedPatientData}>
                                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#64748b', fontSize: 12 }} dy={10} minTickGap={30} />
                                                <YAxis axisLine={false} tickLine={false} tick={{ fill: '#64748b', fontSize: 12 }} />
                                                <Tooltip contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }} />
                                                <Legend verticalAlign="top" height={36} align="right" />
                                                {selectedGender === 'all' ? (
                                                    <>
                                                        <Line type="monotone" dataKey="male" name="Male Patients" stroke="#3b82f6" strokeWidth={4} dot={{ r: 4, fill: '#3b82f6' }} activeDot={{ r: 6 }} animationDuration={1500} isAnimationActive={patientsInView} />
                                                        <Line type="monotone" dataKey="female" name="Female Patients" stroke="#ec4899" strokeWidth={4} dot={{ r: 4, fill: '#ec4899' }} activeDot={{ r: 6 }} animationDuration={1500} isAnimationActive={patientsInView} />
                                                    </>
                                                ) : (
                                                    <Line type="monotone" dataKey="count" name="Patients" stroke="#4f46e5" strokeWidth={4} dot={{ r: 6, fill: '#4f46e5', strokeWidth: 2, stroke: '#fff' }} activeDot={{ r: 8 }} animationDuration={1500} isAnimationActive={patientsInView} />
                                                )}
                                            </LineChart>
                                        </ResponsiveContainer>
                                    ) : !patientsLoading && (
                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#94a3b8' }}>
                                            <FaChartLine style={{ fontSize: '3rem', marginBottom: '1rem', opacity: 0.5 }} />
                                            <p style={{ fontSize: '1.1rem', fontWeight: 500 }}>No patient data available</p>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className={`lifetime-chart-box full-width-chart animate-slide-up ${opdVisible ? 'visible' : ''}`} style={{ marginTop: '2rem' }} ref={opdRef}>
                                <header className="content-header-mini" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <div><h3>OPD Visits Overview</h3><p>Total outpatient department cases</p></div>
                                    <div className="finance-sub-navigation" style={{ margin: 0 }}>
                                        {['daily', 'monthly', 'yearly'].map(filter => <button key={filter} className={`sub-nav-btn ${opdFilter === filter ? 'active' : ''}`} onClick={() => setOpdFilter(filter)} style={{ padding: '0.4rem 0.8rem', fontSize: '0.85rem' }}>{filter.charAt(0).toUpperCase() + filter.slice(1)}</button>)}
                                    </div>
                                </header>
                                <div className="chart-container" style={{ height: 350 }}>
                                    {opdLoading ? <SkeletonLoader variant="chart-placeholder" /> : (opdVisible && (selectedGender === 'all' ? comparisonOpd : processedOpdData).length > 0) ? (
                                        <ResponsiveContainer width="100%" height="100%">
                                            <AreaChart data={selectedGender === 'all' ? comparisonOpd : processedOpdData}>
                                                <defs>
                                                    <linearGradient id="colorOpd" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#10b981" stopOpacity={0.2} /><stop offset="95%" stopColor="#10b981" stopOpacity={0} /></linearGradient>
                                                    <linearGradient id="colorMaleOpd" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#3b82f6" stopOpacity={0.2} /><stop offset="95%" stopColor="#3b82f6" stopOpacity={0} /></linearGradient>
                                                    <linearGradient id="colorFemaleOpd" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#ec4899" stopOpacity={0.2} /><stop offset="95%" stopColor="#ec4899" stopOpacity={0} /></linearGradient>
                                                </defs>
                                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#64748b', fontSize: 12 }} dy={10} minTickGap={30} />
                                                <YAxis axisLine={false} tickLine={false} tick={{ fill: '#64748b', fontSize: 12 }} />
                                                <Tooltip contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)' }} />
                                                <Legend verticalAlign="top" height={36} align="right" />
                                                {selectedGender === 'all' ? (
                                                    <>
                                                        <Area type="monotone" dataKey="male" name="Male OPD" stroke="#3b82f6" strokeWidth={3} fillOpacity={1} fill="url(#colorMaleOpd)" animationDuration={1500} isAnimationActive={opdInView} />
                                                        <Area type="monotone" dataKey="female" name="Female OPD" stroke="#ec4899" strokeWidth={3} fillOpacity={1} fill="url(#colorFemaleOpd)" animationDuration={1500} isAnimationActive={opdInView} />
                                                    </>
                                                ) : (
                                                    <Area type="monotone" dataKey="count" name="OPD Cases" stroke="#10b981" strokeWidth={3} fillOpacity={1} fill="url(#colorOpd)" animationDuration={1500} animationEasing="ease-in-out" isAnimationActive={opdInView} />
                                                )}
                                            </AreaChart>
                                        </ResponsiveContainer>
                                    ) : !opdLoading && (
                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#94a3b8' }}>
                                            <FaChartLine style={{ fontSize: '3rem', marginBottom: '1rem', opacity: 0.5 }} />
                                            <p style={{ fontSize: '1.1rem', fontWeight: 500 }}>No OPD data available</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </section>
                    </div>
                )}

                {activeTab === 'revenue' && (
                    <div className="tab-content-fade-in" ref={revenueRef}>
                        <section className="dashboard-section">
                            <header className="analysis-header">
                                <div className="analysis-title">
                                    <div className="title-icon" style={{ background: '#e0e7ff', color: '#4f46e5' }}><FaChartLine /></div>
                                    <div><h2>Revenue Composition Analytics</h2><p>Detailed breakdown of OPD vs IPD revenue sources</p></div>
                                </div>
                                <div className="analysis-filters" style={{ display: 'flex', gap: '1rem' }}>
                                    <CustomDropdown label="Year" value={revenueYear} onChange={(e) => setRevenueYear(typeof e === 'object' ? e.target.value : e)} options={[{ value: '2024', label: '2024' }, { value: '2025', label: '2025' }, { value: '2026', label: '2026' }]} style={{ minWidth: '130px', backgroundColor: 'white' }} />
                                    <CustomDropdown label="Month" value={revenuePeriod} onChange={(e) => setRevenuePeriod(typeof e === 'object' ? e.target.value : e)} options={monthNames.map((m, i) => ({ value: (i + 1).toString(), label: m }))} style={{ minWidth: '130px', backgroundColor: 'white' }} />
                                </div>
                            </header>

                            <div className="finance-grid" style={{ marginBottom: '2rem' }} ref={revenueGridRef}>
                                <div className={`finance-card animate-slide-up stagger-1 ${revenueGridVisible ? 'visible' : ''}`}>
                                    <div className="finance-card-icon" style={{ backgroundColor: '#e0e7ff', color: '#4f46e5' }}><FaWallet /></div>
                                    <div className="finance-card-info"><p>Total Revenue</p><h3>{formatCurrency(revenueChartData.reduce((acc, curr) => acc + curr.Total, 0))}</h3><span className="card-sub-info">Selected Period</span></div>
                                </div>
                                <div className={`finance-card animate-slide-up stagger-2 ${revenueGridVisible ? 'visible' : ''}`}>
                                    <div className="finance-card-icon" style={{ backgroundColor: '#dcfce7', color: '#10b981' }}><FaChartLine /></div>
                                    <div className="finance-card-info"><p>OPD Contribution</p><h3>{formatCurrency(revenueChartData.reduce((acc, curr) => acc + curr.OPD, 0))}</h3><span className="card-sub-info">Out-Patient Dept</span></div>
                                </div>
                                <div className={`finance-card animate-slide-up stagger-3 ${revenueGridVisible ? 'visible' : ''}`}>
                                    <div className="finance-card-icon" style={{ backgroundColor: '#f3e8ff', color: '#8b5cf6' }}><FaStethoscope /></div>
                                    <div className="finance-card-info"><p>IPD Contribution</p><h3>{formatCurrency(revenueChartData.reduce((acc, curr) => acc + curr.IPD, 0))}</h3><span className="card-sub-info">In-Patient Dept</span></div>
                                </div>
                            </div>

                            <div className={`lifetime-chart-box full-width-chart animate-slide-up stagger-1 ${revenueVisible ? 'visible' : ''}`}>
                                <header className="content-header-mini"><h3>Revenue Composition Trend</h3><p>Comparative view of OPD and IPD revenue</p></header>
                                <div className="chart-container" style={{ height: 400 }}>
                                    {revenueLoading ? <SkeletonLoader variant="chart-placeholder" /> : (revenueVisible && revenueChartData.length > 0) ? (
                                        <ResponsiveContainer width="100%" height="100%">
                                            <BarChart data={revenueChartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#64748b', fontSize: 12 }} dy={10} />
                                                <YAxis axisLine={false} tickLine={false} tick={{ fill: '#64748b', fontSize: 12 }} tickFormatter={(val) => `₹${val / 1000}k`} />
                                                <Tooltip cursor={{ fill: 'rgba(0, 0, 0, 0.02)' }} contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)' }} formatter={(val) => formatCurrency(val)} />
                                                <Legend verticalAlign="top" height={36} iconType="circle" />
                                                {selectedGender === 'all' ? (
                                                    <>
                                                        <Bar dataKey="Male" name="Male Revenue" fill="#3b82f6" radius={[4, 4, 0, 0]} barSize={30} animationDuration={1500} isAnimationActive={revenueInView} />
                                                        <Bar dataKey="Female" name="Female Revenue" fill="#ec4899" radius={[4, 4, 0, 0]} barSize={30} animationDuration={1500} isAnimationActive={revenueInView} />
                                                    </>
                                                ) : (
                                                    <>
                                                        <Bar dataKey="OPD" name="OPD Revenue" fill="#4f46e5" radius={[4, 4, 0, 0]} barSize={30} animationDuration={1500} animationEasing="ease-in-out" isAnimationActive={revenueInView} />
                                                        <Bar dataKey="IPD" name="IPD Revenue" fill="#8b5cf6" radius={[4, 4, 0, 0]} barSize={30} animationDuration={1500} animationEasing="ease-in-out" animationBegin={200} isAnimationActive={revenueInView} />
                                                    </>
                                                )}
                                            </BarChart>
                                        </ResponsiveContainer>
                                    ) : !revenueLoading && (
                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#94a3b8' }}>
                                            <FaChartLine style={{ fontSize: '3rem', marginBottom: '1rem', opacity: 0.5 }} />
                                            <p style={{ fontSize: '1.1rem', fontWeight: 500 }}>No revenue data available</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </section>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminDashboard;
